import {
  Configuration,
  DataItem,
  Dimensions,
  Gutter,
  OptionsMaterial,
  Params,
  Parapet,
  PriceCategory,
  Roof,
  Wall,
} from "src/types/configuration";
import ConfiguratorService from "src/services/configuratorService";
import prices from "src/prices";
import { calculateKapschuurRoofBigSideLength } from "src/helpers/configuration";

class CalculationService {
  private static instance: CalculationService;
  private configuratorService?: ConfiguratorService;
  private state?: Configuration;
  private dimensions?: Dimensions;
  private parapet?: Parapet;
  private roof?: Roof;
  private roofOverhang = 0;
  private gutter?: Gutter;
  private walls?: Wall[];
  private storages?: { left: boolean; right: boolean };
  private data: DataItem[] = [];
  private optionsMaterial: OptionsMaterial | undefined;
  private params: Params | undefined;
  private montageRates: Record<PriceCategory, number> = {
    A: 0.43,
    B: 0.27,
    NVT: 0,
  };
  private readonly montageMinimumPrice = 1350;
  private readonly montageRoundingStep = 25;

  constructor(
    initConfiguration: Configuration,
    configuratorService: ConfiguratorService
  ) {
    if (CalculationService.instance) {
      return CalculationService.instance;
    }

    CalculationService.instance = this;
    this.state = initConfiguration;
    this.configuratorService = configuratorService;
    this.dimensions = initConfiguration.dimensions;
    this.parapet = initConfiguration.parapet;
    this.roof = initConfiguration.roof;
    this.gutter = initConfiguration.gutter;
    this.walls = initConfiguration.walls;
    this.storages = initConfiguration.storages;
    this.optionsMaterial = initConfiguration.optionsMaterial;
    this.params = initConfiguration.params;

    this.data = prices
      .filter((price) => price && price.price > 0)
      .map((p) => {
        return {
          name: p.name,
          label: p.label,
          unit: p.unit,
          price: p.price,
          quantity: 0,
          total: 0,
          category: p.category,
        };
      });

    // this.data.push({
    //   name: "montage",
    //   label: "Montage",
    //   unit: "stuk",
    //   price: 0,
    //   quantity: 0,
    //   total: 0,
    //   category: "NVT",
    // });
  }

  setState(state: Configuration): void {
    this.state = state;
    this.dimensions = state.dimensions;
    this.parapet = state.parapet;
    this.roof = state.roof;
    this.gutter = state.gutter;
    this.walls = state.walls;
    this.storages = state.storages;
    this.optionsMaterial = state.optionsMaterial;
    this.params = state.params;
  }

  getData() {
    return this.data.filter((item) => {
      if (!item) return false;

      // @ts-ignore
      if (this.state.roofType === "platdak") {
        if (item.name === "dakpannen" || item.name === "dakpannen_terracota") {
          return false;
        }
      }

      return true;
    });
  }

  isKapschuur() {
    return this.state?.roofType === "kapschuur";
  }

  getEPDMGlue() {
    const item = this.getPriceItem("epdmGlue");
    return item ? item.price * this.calculatePlatdakRoofSquareInSqMeters() : 0;
  }

  getBlackTrim() {
    const item = this.getPriceItem("daktrim_zwart");
    return item ? item.price * this.getRoofPerimeterInMeters() : 0;
  }

  getAluminiumTrim() {
    const item = this.getPriceItem("daktrim_blank");
    return item ? item.price * this.getRoofPerimeterInMeters() : 0;
  }

  getPoer() {
    const item = this.getPriceItem("betonproer");
    if (!item) return 0;
    if (!this.optionsMaterial?.poerQuantity) return item.price;
    const quantity =
      this.optionsMaterial.poerQuantity > 0 &&
        this.optionsMaterial.poer === "poer"
        ? this.optionsMaterial.poerQuantity
        : 1;
    return item.price * quantity;
  }

  getPoertegel() {
    const item = this.getPriceItem("betontegel");
    if (!item) return 0;
    if (!this.optionsMaterial?.poerQuantity) return item.price;
    const quantity =
      this.optionsMaterial.poerQuantity > 0 &&
        this.optionsMaterial.poer === "poertegel"
        ? this.optionsMaterial.poerQuantity
        : 1;
    return item.price * quantity;
  }

  getLiterBeitsWit() {
    const item = this.getPriceItem("liter_beits_wit");
    if (!item) return 0;
    if (!this.optionsMaterial?.liter_beits_wit) return item.price;
    const quantity =
      this.optionsMaterial.liter_beits_wit > 0
        ? this.optionsMaterial.liter_beits_wit
        : 1;
    return item ? item.price * quantity : 0;
  }

  getLiterBeitsZwart() {
    const item = this.getPriceItem("liter_beits_zwart");
    if (!item) return 0;
    if (!this.optionsMaterial?.liter_beits_zwart) return item.price;
    const quantity =
      this.optionsMaterial.liter_beits_zwart > 0
        ? this.optionsMaterial.liter_beits_zwart
        : 1;
    return item.price * quantity;
  }

  getLiterBeitsGray() {
    const item = this.getPriceItem("liter_beits_gray");
    if (!item) return 0;
    if (!this.optionsMaterial?.liter_beits_gray) return item.price;
    const quantity =
      this.optionsMaterial.liter_beits_gray > 0
        ? this.optionsMaterial.liter_beits_gray
        : 1;
    return item.price * quantity;
  }

  getLiterBeitsDouglas() {
    const item = this.getPriceItem("liter_beits_douglas");
    if (!item) return 0;
    if (!this.optionsMaterial?.liter_beits_douglas) return item.price;
    const quantity =
      this.optionsMaterial.liter_beits_douglas > 0
        ? this.optionsMaterial.liter_beits_douglas
        : 1;
    return item.price * quantity;
  }

  getHemelwaterafvoerPvc() {
    if (this.isKapschuur()) return 0;

    const hwaItem = this.getPriceItem("hemelwaterafvoer_pvc");

    // 2 stuk if width <= 6m, 4 stuk if width > 6m
    const quantity = (this.dimensions?.width ?? 0) > 6000 ? 2 : 1;
    const hwaTotal = hwaItem ? hwaItem.price * quantity : 0;

    return hwaTotal;
  }

  getGutterPrice() {
    if (!this.isKapschuur()) return 0;

    const dakgootItem = this.getPriceItem("dakgoot_zinkkleur");
    const hemelwaterItem = this.getPriceItem("regenwaterafvoer_zinkkleur");
    const width = this.dimensions?.width ?? 0;

    const dakgootQuantity = this.toMeter(width * 2);
    const hemelwaterQuantity = 2;
    const dakgootTotal = dakgootItem ? dakgootItem.price * dakgootQuantity : 0;
    const hemelwaterTotal = hemelwaterItem
      ? hemelwaterItem.price * hemelwaterQuantity
      : 0;

    return dakgootTotal + hemelwaterTotal;
  }

  getBetonbandAntraciet() {
    const item = this.getPriceItem("betonband_antraciet");
    if (!item) return 0;
    if (!this.optionsMaterial?.betonband_antraciet) return item.price;
    const quantity =
      this.optionsMaterial.betonband_antraciet > 0
        ? this.optionsMaterial.betonband_antraciet
        : 1;
    return item.price * quantity;
  }

  getMontagePrice() {
    // if (!this.shouldApplyMontage()) {
    //   return 0;
    // }

    return this.calculateMontagePrice();
  }

  getRoofingPrice(name: string) {
    if (!this.data || !this.state) return 0;

    const kapschuurDepthRoofSide =
      this.state.params.kapschuurOverhangLength +
      calculateKapschuurRoofBigSideLength(this.state);
    const width = this.dimensions?.width ?? 0;
    const quantity = this.toSqMeter(width * kapschuurDepthRoofSide);
    let price = 0;

    if (name === "dakpanplaat") {
      price = this.getPriceItem("dakpanprofielen-zwart")?.price ?? 0;
    }

    if (name === "dakpanplaat_rood") {
      price = this.getPriceItem("dakpanprofielen-terracotta")?.price ?? 0;
    }

    if (name === "leiendak") {
      price = this.getPriceItem("shingles-zwart")?.price ?? 0;
    }

    if (name === "leiendak_red") {
      price = this.getPriceItem("shingles-rood")?.price ?? 0;
    }

    if (name === "leiendak_green") {
      price = this.getPriceItem("shingles-groen")?.price ?? 0;
    }

    return price * quantity;
  }

  roundValue(num: number, precision: number = 2) {
    return parseFloat(num.toFixed(precision));
  }

  formatToCurrency(price: number) {
    return price.toLocaleString("nl-NL", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  calculateTotalPrice(): number {
    let total = this.data.reduce((acc, item) => {
      if (!item || item.quantity === 0) return acc;
      return acc + item.total;
    }, 0);

    let montagePrice = 0;
    if (this?.state?.optionsMaterial?.montage)
      montagePrice = this.getMontagePrice();

    const montageAlreadyCounted = this.data.some(
      (row: DataItem) =>
        row && row.name === "montage" && row.quantity && row.quantity > 0
    );

    total = montageAlreadyCounted ? total : total + montagePrice;

    return total;
  }

  getFormatTotalPrice(): string {
    return this.formatToCurrency(this.calculateTotalPrice());
  }

  toMeter = (value: number): number => {
    const meters = value / 1000;

    return parseFloat(meters.toFixed(2));
  };

  toSqMeter(value: number): number {
    const meters = value / 1_000_000;

    return parseFloat(meters.toFixed(2));
  }

  private roundUpToIncrement(value: number, increment: number): number {
    if (increment <= 0) return value;
    return Math.ceil(value / increment) * increment;
  }

  private roundLiggerLengthMm(baseDepthMm: number, lengthMm: number): number {
    if (lengthMm <= 0) return 0;
    const increment = baseDepthMm <= 2500 ? 500 : 1000;
    return this.roundUpToIncrement(lengthMm, increment);
  }

  private roundRabatPlankLengthMeters(lengthMeters: number): number {
    if (lengthMeters <= 0) return 0;
    const increment = lengthMeters <= 2.5 ? 0.5 : 1;
    return this.roundUpToIncrement(lengthMeters, increment);
  }

  private getPlankHeightMeters() {
    return 0.175;
  }

  private getKapschuurSideWallOpenHeightMm(defaultHeightMm: number) {
    const highestWallPointMm = window.CONF?.house?.previousHeight;
    if (
      typeof highestWallPointMm === "number" &&
      Number.isFinite(highestWallPointMm) &&
      highestWallPointMm > 0
    ) {
      return highestWallPointMm;
    }

    const depthMm = this.dimensions?.depth ?? 0;
    const bigHeightMm = this.state?.params.kapschuurBigHeight ?? defaultHeightMm;
    const roofBigAngleDeg = this.state?.params.kapschuurOverhangBigAngle ?? 20;

    if (depthMm <= 0 || bigHeightMm <= 0) {
      return defaultHeightMm;
    }

    const angleRad = (roofBigAngleDeg * Math.PI) / 180;
    const heightScaled = bigHeightMm / 1000;
    const depthScaled = depthMm / 1000;
    const heightThoughDepth =
      Math.tan(angleRad) * (depthScaled + 0.05575) - Math.tan(angleRad) * 3.05575;
    const frontHeightRatio = 1 + heightThoughDepth / heightScaled;

    return Math.max(0, bigHeightMm * frontHeightRatio);
  }

  private getWallPlankInputs(wall: Wall, defaultHeightMm: number, depthMm: number) {
    let wallWidthMm = this.configuratorService?.getWallWidth(wall) ?? 0;
    let wallHeightMm = defaultHeightMm;
    let plankCountAdjustment = 0;

    if (this.isKapschuur()) {
      const kapschuurOpenHeightMm =
        this.getKapschuurSideWallOpenHeightMm(defaultHeightMm);

      if (wall.id.startsWith("h-1")) {
        wallHeightMm = this.state?.params.kapschuurSmallHeight ?? defaultHeightMm;
      }

      if (wall.id.startsWith("h-0")) {
        // Front wall uses full open kapschuur height (no -1 plank correction).
        wallHeightMm = kapschuurOpenHeightMm;
      }

      if (wall.id.startsWith("v-")) {
        // Side walls use the same height as front, but minus one plank.
        wallHeightMm = kapschuurOpenHeightMm;
        wallWidthMm = depthMm - 2 * (this.params?.kapschuurColumn || 0);
        plankCountAdjustment = -1;
      }
    }

    return { wallWidthMm, wallHeightMm, plankCountAdjustment };
  }

  private getWallPlankMetrics(
    tempWidthMm: number,
    tempHeightMm: number,
    plankCountAdjustment = 0
  ) {
    if (tempWidthMm <= 0 || tempHeightMm <= 0) {
      return { plankLengthMeters: 0, plankCount: 0, totalLinearMeters: 0 };
    }

    const wallWidthMeters = tempWidthMm / 1000;
    const wallHeightMeters = tempHeightMm / 1000;
    const plankLengthMeters = this.roundRabatPlankLengthMeters(wallWidthMeters);
    const plankHeightMeters = this.getPlankHeightMeters();
    const plankCount = Math.max(
      0,
      Math.ceil(wallHeightMeters / plankHeightMeters) + plankCountAdjustment
    );
    const totalLinearMeters = plankLengthMeters * plankCount;

    return { plankLengthMeters, plankCount, totalLinearMeters };
  }

  private calculateRabatLinearMeters(
    tempWidthMm: number,
    tempHeightMm: number,
    plankCountAdjustment = 0
  ): number {
    return this.getWallPlankMetrics(
      tempWidthMm,
      tempHeightMm,
      plankCountAdjustment
    ).totalLinearMeters;
  }

  private calculateRabatQuantity(
    color: string,
    height: number,
    depth: number,
    isCommonWall: boolean
  ): number {
    let quantity = 0;
    let commonWall = false;

    this.walls?.forEach((wall) => {
      if (wall.id === "v-0-1" && commonWall) {
        return;
      }

      if (wall.id === "v-0-1" && isCommonWall) {
        commonWall = true;
      }

      const { wallWidthMm, wallHeightMm, plankCountAdjustment } =
        this.getWallPlankInputs(wall, height, depth);

      if (wall.woodWall.outerColor === color) {
        quantity += this.calculateRabatLinearMeters(
          wallWidthMm,
          wallHeightMm,
          plankCountAdjustment
        );
      }

      if (wall.woodWall.innerColor === color) {
        quantity += this.calculateRabatLinearMeters(
          wallWidthMm,
          wallHeightMm,
          plankCountAdjustment
        );
      }
    });

    return quantity;
  }

  getWallPlankBreakdownRows() {
    if (!this.walls || !this.state || !this.dimensions) return [];

    const columns = this.configuratorService?.getColumns() ?? 0;
    const height = this.dimensions.height ?? 0;
    const depth = this.dimensions.depth ?? 0;
    const isCommonWall =
      this.storages?.left && this.storages?.right ? columns === 2 : false;
    const supportedWallTypes = new Set([
      "wall_potdeksel",
      "wall_potdeksel_black",
      "wall_vuren_black",
      "wall_blockhut",
      "wall_blockhut_black",
    ]);
    const rows: Array<{
      parentName: string;
      label: string;
      unit: string;
      quantity: number;
    }> = [];
    let commonWall = false;

    this.walls.forEach((wall) => {
      if (wall.id === "v-0-1" && commonWall) {
        return;
      }

      if (wall.id === "v-0-1" && isCommonWall) {
        commonWall = true;
      }

      const { wallWidthMm, wallHeightMm, plankCountAdjustment } =
        this.getWallPlankInputs(wall, height, depth);

      const sides = [
        { color: wall.woodWall.outerColor, sideLabel: "buiten" },
        { color: wall.woodWall.innerColor, sideLabel: "binnen" },
      ];

      sides.forEach((side) => {
        if (!supportedWallTypes.has(side.color)) return;

        const metrics = this.getWallPlankMetrics(
          wallWidthMm,
          wallHeightMm,
          plankCountAdjustment
        );
        if (metrics.plankCount <= 0 || metrics.plankLengthMeters <= 0) return;

        const lengthLabel = this.roundValue(metrics.plankLengthMeters, 2);
        rows.push({
          parentName: side.color,
          label: `Wand ${wall.id} ${side.sideLabel} - Wandplank ${lengthLabel}m`,
          unit: "stuk",
          quantity: metrics.plankCount,
        });
      });
    });

    return rows;
  }

  calculateRoofSideLength() {
    const base = this.dimensions?.depth ?? 0;
    const angle = this.dimensions?.angle ?? 40;
    const angleInRadians = (angle * Math.PI) / 180;
    const sideLength =
      (base + this.roofOverhang) / (2 * Math.cos(angleInRadians));

    return parseFloat(sideLength.toFixed(2));
  }

  calculateRoofSquareInSqMeters() {
    const width = this.dimensions?.width ?? 0;
    const sideLength = this.calculateRoofSideLength();

    return this.toSqMeter(2 * sideLength * (width + this.roofOverhang));
  }

  calculatePlatdakRoofSquareInSqMeters() {
    const width = this.getWidthWithOverhang();
    const depth = this.getDepthWithOverhang();
    return this.toSqMeter(width * depth);
  }

  calculateTriangleAreaInSqMeters() {
    const base = this.dimensions?.depth ?? 0;
    const sideLength = this.calculateRoofSideLength();

    return this.toSqMeter(
      (1 / 2) * base * Math.sqrt(4 * sideLength ** 2 - base ** 2)
    );
  }

  getCountVerticalBalks() {
    return this.configuratorService?.getColumnsBalks() ?? 0;
  }

  getWidthWithOverhang() {
    const width = this.dimensions?.width ?? 0;
    const overhangLeft = this.dimensions?.overhangLeft ?? 0;
    const overhangRight = this.dimensions?.overhangRight ?? 0;
    return width + overhangLeft + overhangRight;
  }

  getDepthWithOverhang() {
    const depth = this.dimensions?.depth ?? 0;
    const overhangBack = this.dimensions?.overhangBack ?? 0;
    const overhangFront = this.dimensions?.overhangFront ?? 0;
    return depth + overhangBack + overhangFront;
  }

  getRoofPerimeterInMeters() {
    const width = this.getWidthWithOverhang() * 2;
    const depth = this.getDepthWithOverhang() * 2;
    return this.toMeter(width + depth);
  }

  getPriceItem(key: string) {
    return this.data.find((item) => item && item.name === key);
  }

  calculateConfiguration(): DataItem[] {
    if (!this.data || !this.state) return [];

    const columns = this.configuratorService?.getColumns() ?? 0;
    const width = this.dimensions?.width ?? 0;
    const widthWithOverhang =
      width +
      (this.dimensions?.overhangLeft ?? 0) +
      (this.dimensions?.overhangRight ?? 0);
    const depth = this.dimensions?.depth ?? 0;
    const depthWithOverhang =
      depth +
      (this.dimensions?.overhangBack ?? 0) +
      (this.dimensions?.overhangFront ?? 0);
    const height = this.dimensions?.height ?? 0;
    const overhangLeft = this.dimensions?.overhangLeft ?? 0;
    const overhangRight = this.dimensions?.overhangRight ?? 0;
    const literBeitsWit = this.optionsMaterial?.liter_beits_wit ?? 0;
    const literBeitsZwart = this.optionsMaterial?.liter_beits_zwart ?? 0;
    const literBeitsGray = this.optionsMaterial?.liter_beits_gray ?? 0;
    const literBeitsDouglas = this.optionsMaterial?.liter_beits_douglas ?? 0;
    const daktrim_blank = this.optionsMaterial?.daktrim_blank ?? 0;
    const daktrim_zwart = this.optionsMaterial?.daktrim_zwart ?? 0;
    const hemelwaterafvoerPvc =
      this.optionsMaterial?.hemelwaterafvoer_pvc ?? false;
    const betonbandAntraciet = this.optionsMaterial?.betonband_antraciet ?? 0;
    const kapschuurDepthRoofSide =
      this.state.params.kapschuurOverhangLength +
      calculateKapschuurRoofBigSideLength(this.state);
    const isCommonWall =
      this.storages?.left && this.storages?.right ? columns === 2 : false;

    // @ts-ignore
    this.data = this.data.map((item) => {
      if (!item) return item;

      if (item.name === "betonproer") {
        const quantity =
          this.optionsMaterial?.poer === "poer"
            ? this.optionsMaterial?.poerQuantity
            : 0;

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "betontegel") {
        const quantity =
          this.optionsMaterial?.poer === "poertegel"
            ? this.optionsMaterial?.poerQuantity
            : 0;

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "staanders") {
        const quantity = this.getCountVerticalBalks();

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "ringbalk") {
        let quantity = this.getRoofPerimeterInMeters(); // perimeter

        this.walls?.forEach((wall) => {
          if (wall.inner) {
            quantity += this.toMeter(depth);
          }
        });

        quantity = Math.round(quantity / 4);

        if (this.isKapschuur()) {
          quantity = width * 3; // 3 bulks - 2 front and 1 back
          quantity = this.toMeter(quantity);
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "gordingbalken") {
        // step 600 mm
        const roundedDepthWithOverhang = this.roundLiggerLengthMm(
          depth,
          depthWithOverhang
        );
        let quantityMm =
          Math.round(widthWithOverhang / 600) * roundedDepthWithOverhang;

        // Calculate corner vertical balks at the big vertical balks
        // 80 cm - length 1 corner vertical balks
        // quantity += this.configuratorService ? this.configuratorService?.getEmptyWallsCount() * 2 * 800 : 0;

        // Calculate Overstek Links + Overstek Rechts
        quantityMm += 4 * (overhangLeft + overhangRight);
        let quantity = this.toMeter(quantityMm);

        if (this.isKapschuur()) {
          const roundedKapschuurDepth = this.roundLiggerLengthMm(
            depth,
            kapschuurDepthRoofSide
          );
          quantityMm =
            Math.round(widthWithOverhang / 500) * roundedKapschuurDepth;
          // @ts-ignore
          quantityMm += this.configuratorService?.getColumns() * 751; // 751 mm - length overhang shoren
          // @ts-ignore
          quantityMm +=
            (this.configuratorService?.getEmptyWallsCount() || 0) * 2 * 743; // 743 mm - other shoren
          quantity = this.toMeter(quantityMm);
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "boeideel") {
        let quantity = this.getRoofPerimeterInMeters() * 2; // perimeter * 2

        if (this.isKapschuur()) {
          quantity = kapschuurDepthRoofSide * 2 + width; // left and right + back
          quantity = this.toMeter(quantity);
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      // carcas wall and gable
      if (item.name === "wood_frame") {
        const round250Or100cm = (mm: number) =>
          mm < 2500 ? 2500 : Math.ceil(mm / 1000) * 1000;

        const woodFrameQty = (sectionWidthMm: number, heightMm: number) => {
          const swRounded = round250Or100cm(sectionWidthMm);
          const heightRounded = round250Or100cm(heightMm);
          const sections60cm = Math.max(1, Math.ceil(sectionWidthMm / 600));
          return 2 * swRounded + sections60cm * heightRounded;
        };

        let quantityMm = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          const tempWidth = this.configuratorService?.getWallWidth(wall) ?? 0;

          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          quantityMm += woodFrameQty(tempWidth, height);
        });

        let quantity = this.toMeter(quantityMm);

        if (this.isKapschuur()) {
          quantityMm = 0;
          commonWall = false;

          this.walls?.forEach((wall) => {
            let tempWidth = this.configuratorService?.getWallWidth(wall) ?? 0;
            let tempHeight = height;

            if (wall.id === "v-0-1" && commonWall) {
              return;
            }

            if (wall.id === "v-0-1" && isCommonWall) {
              commonWall = true;
            }

            if (wall.id.startsWith("v-")) {
              // @ts-ignore
              tempWidth = depth - 2 * (this.params?.kapschuurColumn || 0);
            }

            if (wall.id.startsWith("h-1")) {
              // @ts-ignore
              tempHeight = this.state?.params.kapschuurSmallHeight ?? height;
            }
            if (wall.id.startsWith("h-0")) {
              // @ts-ignore
              tempHeight = this.state?.params.kapschuurBigHeight ?? height;
            }
            if (wall.id.startsWith("v-")) {
              // @ts-ignore
              tempHeight =
                ((this.state?.params.kapschuurBigHeight || 0) +
                  (this.state?.params.kapschuurSmallHeight || 0)) /
                2;
            }

            quantityMm += woodFrameQty(tempWidth, tempHeight);
          });

          quantity = this.toMeter(quantityMm);
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "wall_potdeksel_black") {
        const quantity = this.calculateRabatQuantity(
          "wall_potdeksel_black",
          height,
          depth,
          isCommonWall
        );

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "wall_potdeksel") {
        const quantity = this.calculateRabatQuantity(
          "wall_potdeksel",
          height,
          depth,
          isCommonWall
        );

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "wall_blockhut") {
        const quantity = this.calculateRabatQuantity(
          "wall_blockhut",
          height,
          depth,
          isCommonWall
        );

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "wall_blockhut_black") {
        const quantity = this.calculateRabatQuantity(
          "wall_blockhut_black",
          height,
          depth,
          isCommonWall
        );

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "wall_vuren_black") {
        const quantity = this.calculateRabatQuantity(
          "wall_vuren_black",
          height,
          depth,
          isCommonWall
        );

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "dichte_73cm_right") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          if (
            wall.woodWall.door === "dichte_73cm" &&
            wall.woodWall.doorSide === "left"
          ) {
            quantity++;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "dichte_73cm_left") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          if (
            wall.woodWall.door === "dichte_73cm" &&
            wall.woodWall.doorSide === "right"
          ) {
            quantity++;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "dichte_100cm_right") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          if (
            wall.woodWall.door === "dichte_100cm" &&
            wall.woodWall.doorSide === "left"
          ) {
            quantity++;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "dichte_100cm_left") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          if (
            wall.woodWall.door === "dichte_100cm" &&
            wall.woodWall.doorSide === "right"
          ) {
            quantity++;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "stapeldorpeldeur_right") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          if (
            wall.woodWall.door === "stapeldorpeldeur" &&
            wall.woodWall.doorSide === "left"
          ) {
            quantity++;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "stapeldorpeldeur_left") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          if (
            wall.woodWall.door === "stapeldorpeldeur" &&
            wall.woodWall.doorSide === "right"
          ) {
            quantity++;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "dubbele_dichte") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          if (wall.woodWall.door === "dubbele_dichte") {
            quantity++;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "dubble_stapeldorpeldeur") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          if (wall.woodWall.door === "dubble_stapeldorpeldeur") {
            quantity++;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "raam") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          if (wall.woodWall.window === "raam") {
            quantity++;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "vleugelraam") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          if (wall.woodWall.window === "vleugelraam") {
            quantity++;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "dakpanprofielen-zwart") {
        let quantity = 0;

        if (this.isKapschuur() && this.state?.roof?.type === "dakpanplaat") {
          quantity = this.toSqMeter(width * kapschuurDepthRoofSide);
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "dakpanprofielen-terracotta") {
        let quantity = 0;

        if (
          this.isKapschuur() &&
          this.state?.roof?.type === "dakpanplaat_rood"
        ) {
          quantity = this.toSqMeter(width * kapschuurDepthRoofSide);
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "shingles-zwart") {
        let quantity = 0;

        if (this.isKapschuur() && this.state?.roof?.type === "leiendak") {
          quantity = this.toSqMeter(width * kapschuurDepthRoofSide);
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "shingles-rood") {
        let quantity = 0;

        if (this.isKapschuur() && this.state?.roof?.type === "leiendak_red") {
          quantity = this.toSqMeter(width * kapschuurDepthRoofSide);
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "shingles-groen") {
        let quantity = 0;

        if (this.isKapschuur() && this.state?.roof?.type === "leiendak_green") {
          quantity = this.toSqMeter(width * kapschuurDepthRoofSide);
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "dakbeschot") {
        let quantity = 0;

        if (this.isKapschuur()) {
          quantity = this.toSqMeter(width * kapschuurDepthRoofSide);
        } else {
          const depthWithAllowance = depth + 270;
          const leftStorageWidth = this.storages?.left
            ? this.dimensions?.leftStorageWidth ?? 0
            : 0;
          const rightStorageWidth = this.storages?.right
            ? this.dimensions?.rightStorageWidth ?? 0
            : 0;
          const bergingWidths = [leftStorageWidth, rightStorageWidth].filter(
            (storageWidth) => storageWidth > 0
          );
          const bergingAreaMm2 = bergingWidths.reduce(
            (sum, storageWidth) =>
              sum +
              this.roundUpToIncrement(storageWidth, 1000) * depthWithAllowance,
            0
          );
          const nonBergingWidth = Math.max(
            width - leftStorageWidth - rightStorageWidth,
            0
          );
          const luifelAreaMm2 =
            nonBergingWidth > 0
              ? this.roundUpToIncrement(nonBergingWidth, 1000) *
              depthWithAllowance
              : 0;
          const totalAreaMm2 = (bergingAreaMm2 + luifelAreaMm2) * 1.07;
          quantity = this.toSqMeter(totalAreaMm2);
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "glaselement") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          if (wall.slidingGlassWall === "glass_door_2m") {
            quantity += 2;
          }

          if (wall.slidingGlassWall === "glass_door_3m") {
            quantity += 3;
          }

          if (wall.slidingGlassWall === "glass_door_4m") {
            quantity += 4;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "glasbok_zwart") {
        let quantity = 0;
        this.walls?.forEach((wall) => {
          if (
            wall.slidingGlassWall !== "empty" &&
            wall.slidingGlassWallOptions.glasbok > 0 &&
            wall.slidingGlassWallOptions.glassColor === "zwart"
          ) {
            quantity += wall.slidingGlassWallOptions.glasbok;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "glasbok_antraciet") {
        let quantity = 0;
        this.walls?.forEach((wall) => {
          if (
            wall.slidingGlassWall !== "empty" &&
            wall.slidingGlassWallOptions.glasbok > 0 &&
            wall.slidingGlassWallOptions.glassColor === "antraciet"
          ) {
            quantity += wall.slidingGlassWallOptions.glasbok;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "glasbok_wit") {
        let quantity = 0;
        this.walls?.forEach((wall) => {
          if (
            wall.slidingGlassWall !== "empty" &&
            wall.slidingGlassWallOptions.glasbok > 0 &&
            wall.slidingGlassWallOptions.glassColor === "wit"
          ) {
            quantity += wall.slidingGlassWallOptions.glasbok;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "dakgoot_zinkkleur") {
        let quantity = 0;

        if (this.isKapschuur() && this.gutter?.enabled) {
          quantity += this.toMeter(width * 2);
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "regenwaterafvoer_zinkkleur") {
        let quantity = 0;

        if (this.isKapschuur() && this.gutter?.enabled) {
          // 2 stuk if width <= 6m, 4 stuk if width > 6m
          quantity = 2;
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "dakgoot_zink_zwart") {
        let quantity = 0;

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "regenwaterafvoer_zink_zwart") {
        let quantity = 0;

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "epdmGlue") {
        let quantity = 0;

        if (!this.isKapschuur() && this.roof?.epdmGlue === true) {
          quantity = this.calculatePlatdakRoofSquareInSqMeters();
        } else {
          quantity = 0;
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "daktrim_blank") {
        let quantity = 0;

        if (!this.isKapschuur() && daktrim_blank === true) {
          quantity = this.getRoofPerimeterInMeters();
        } else {
          quantity = 0;
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "daktrim_zwart") {
        let quantity = this.getRoofPerimeterInMeters();

        if (this.isKapschuur() || daktrim_zwart !== true) {
          quantity = 0;
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "liter_beits_zwart") {
        let quantity = 0;

        if (literBeitsZwart) {
          quantity = literBeitsZwart;
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "liter_beits_wit") {
        let quantity = 0;

        if (literBeitsWit) {
          quantity = literBeitsWit;
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "liter_beits_gray") {
        let quantity = 0;

        if (literBeitsGray) {
          quantity = literBeitsGray;
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "liter_beits_douglas") {
        let quantity = 0;

        if (literBeitsDouglas) {
          quantity = literBeitsDouglas;
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "hemelwaterafvoer_pvc") {
        let quantity = 0;

        if (!this.isKapschuur() && hemelwaterafvoerPvc) {
          // 2 stuk if width <= 6m, 4 stuk if width > 6m
          quantity = (this.dimensions?.width ?? 0) > 6000 ? 2 : 1;
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "betonband_antraciet") {
        let quantity = 0;

        if (betonbandAntraciet) {
          quantity = betonbandAntraciet;
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "doorBrushes") {
        let quantity = 0;

        this.walls?.forEach((wall) => {
          if (wall.slidingGlassWallOptions.doorBrushes) {
            quantity++;
          }
        });
        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "stainlessSteelHandle") {
        let quantity = 0;
        this.walls?.forEach((wall) => {
          if (wall.slidingGlassWallOptions.stainlessSteelHandle) {
            quantity++;
          }
        });
        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "lockProfile") {
        let quantity = 0;
        this.walls?.forEach((wall) => {
          if (wall.slidingGlassWallOptions.lockProfile) {
            quantity++;
          }
        });
        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "schuifrichting") {
        let quantity = 0;
        this.walls?.forEach((wall) => {
          if (wall.slidingGlassWallOptions.schuifrichting) {
            quantity++;
          }
        });
        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "contributors") {
        let quantity = 0;
        this.walls?.forEach((wall) => {
          if (wall.slidingGlassWallOptions.contributors) {
            quantity++;
          }
        });
        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "bevestigingsmaterialen") {
        return {
          ...item,
          quantity: 0,
          total: 0,
        };
      }
    });

    const bevestigingsItem = this.data.find(
      (row) => row && row.name === "bevestigingsmaterialen"
    );
    if (bevestigingsItem) {
      const baseTotal = this.data.reduce((sum, row) => {
        if (!row || row.name === "bevestigingsmaterialen") {
          return sum;
        }
        if (row.category !== "A" && row.category !== "B") {
          return sum;
        }
        return sum + row.total;
      }, 0);
      const calculated =
        baseTotal > 0 ? Math.max(45, baseTotal * 0.02) : 0;
      const rounded = this.roundValue(calculated);
      bevestigingsItem.quantity = rounded > 0 ? 1 : 0;
      bevestigingsItem.price = rounded;
      bevestigingsItem.total = rounded;
    }

    this.applyMontageRow();

    return this.data;
  }

  private shouldApplyMontage() {
    return this.optionsMaterial?.montage === true;
  }

  private getMontageMultiplier(category?: PriceCategory) {
    if (!category) {
      return 0;
    }

    return this.montageRates[category] ?? 0;
  }

  private calculateMontageSurchargeFromData() {
    // if (!this.shouldApplyMontage()) {
    //   return 0;
    // }

    return this.data.reduce((sum, item) => {
      if (!item || item.name === "montage") {
        return sum;
      }

      const multiplier = this.getMontageMultiplier(item.category);
      if (!multiplier || item.total <= 0) {
        return sum;
      }

      return sum + item.total * multiplier;
    }, 95);
  }

  private calculateMontagePrice() {
    const surchargeWithMinimum = Math.max(
      this.calculateMontageSurchargeFromData(),
      this.montageMinimumPrice
    );

    return Math.ceil(surchargeWithMinimum / this.montageRoundingStep) * this.montageRoundingStep;
  }

  private applyMontageRow() {
    if (!this.shouldApplyMontage()) {
      return;
    }

    const price = this.calculateMontagePrice();

    this.data.push({
      name: "montage",
      label: "Montage",
      unit: "stuk",
      price,
      quantity: 1,
      total: price,
    });
  }
}

export default CalculationService;
